<script lang="ts">
	import { t } from '$i18n/i18n'
	import type { AutocompleteFieldConfig, SvelecteControlApi } from '$types/autocompleteFieldConfig'
	import { AutocompleteConfig } from '$types/autocompleteFieldConfig'
	import type { MaterialProperty, MaterialPropertyListElement } from './types/materialProperty'
	import { dropdownTypes } from './types/materialProperty'
	import AutocompleteInputField from '$lib/autocomplete/AutocompleteInputField.svelte'
	import type { FontProps } from '$types/fontProps'
	import { DefaultInputFieldProps } from '$types/fontProps'
	import { breadcrumbs } from '$lib/organisms/Breadcrumbs/breadcrumbs.svelte'
	import { mmpvState } from './mmpv.state.svelte'

	interface Props {
		materialProperty?: MaterialProperty | null
		propertyListElement?: MaterialPropertyListElement
		alreadyMappedProperties?: MaterialProperty[]
	}

	let {
		materialProperty = $bindable(),
		propertyListElement = undefined,
		alreadyMappedProperties = []
	}: Props = $props()

	// we need to introduce this variable because otherwise the property would be set to null from the AC-component, and then we would get NPEs
	// we transfer the selected data from the acProperty to the property on:change
	let acProperty: MaterialProperty | undefined = $state()
	// svelecte instance, so an external clear can be pushed into svelecte imperatively
	let svelecte: SvelecteControlApi | null = $state(null)

	const propertyAcConfig: AutocompleteFieldConfig = AutocompleteConfig('id', 'name')
	propertyAcConfig.valueAsObject = true
	propertyAcConfig.searchProps = { skipSort: false }
	const fontProps: FontProps = DefaultInputFieldProps()

	// base option list, computed once at init (intentionally non-reactive)
	const baseOptions = computeBaseOptions()
	let propertyOptions: MaterialProperty[] = $derived(
		baseOptions.filter(
			(option) => !alreadyMappedProperties.some((mappedProperty) => mappedProperty.id === option.id)
		)
	)

	/**
	 * mirror an external clear into the input field. The AC value lives in the local
	 * `acProperty` and is otherwise only set by user selection, so resetting the bound
	 * `materialProperty` to null from the parent would leave the previous selection showing.
	 * Setting bind:value alone does NOT reset svelecte's rendered selection (it lingers behind
	 * the now-collapsed label), so also clear it imperatively — setSelection(null) is svelecte's
	 * own clear. Only depends on (reads) `materialProperty`, so it does not fight user selection.
	 */
	$effect(() => {
		if (materialProperty == null) {
			svelecte?.setSelection(null, false) // this resets the selection in svelecte internally
			acProperty = undefined
		}
	})

	function computeBaseOptions(): MaterialProperty[] {
		let options = [...mmpvState.materialProperties.values()]
		// filter properties by circular dependency if parent Component is from property modal
		if (propertyListElement !== undefined) {
			// filter with store properties
			options = options.filter((property) => !isCyclic({ ...property }))
			// filter with not saved properties in breadcrumb
			let breadcrumbProperties: string[] = []
			breadcrumbs.get().forEach((breadcrumb) => {
				if (breadcrumb.data.materialProperty !== undefined) {
					breadcrumbProperties.push(breadcrumb.data.materialProperty.id)
				}
			})
			// filter dropdown value properties which are mapped in breadcrumbs
			options = options.filter((property) => !breadcrumbProperties.includes(property.id))
		}
		return options
	}

	function writeSelectedToProperty() {
		if (acProperty) {
			materialProperty = { ...acProperty }
		} else {
			materialProperty = undefined
		}
	}

	function isCyclicUtil(
		property: MaterialProperty,
		visited: Map<string, MaterialProperty>,
		rec: Map<string, MaterialProperty>
	): boolean {
		let returnHelper = false
		if (rec.has(property.id)) {
			return true
		}
		if (visited.has(property.id)) {
			return false
		}

		visited.set(property.id, property)
		rec.set(property.id, property)
		let childProperties: MaterialProperty[] = []
		property.propertyListElements.forEach((propertyListElement: MaterialPropertyListElement) =>
			propertyListElement.propertyChildIds.forEach((propChildId: string) => {
				const prop = mmpvState.getMaterialProperty(propChildId)
				if (prop && dropdownTypes.includes(prop.type)) {
					childProperties.push({ ...prop })
				}
			})
		)

		childProperties.forEach((prop) => {
			if (isCyclicUtil({ ...prop }, visited, rec)) {
				returnHelper = true
				return true
			}
		})
		rec.delete(property.id)
		return returnHelper
	}

	function isCyclic(property: MaterialProperty): boolean {
		let visited: Map<string, MaterialProperty> = new Map()
		let stack: Map<string, MaterialProperty> = new Map()

		let parentPropWithChild = $state.snapshot(materialProperty) as MaterialProperty | undefined
		if (!parentPropWithChild) {
			return false
		}

		let indexOfPropertyListElement =
			propertyListElement === undefined
				? -1
				: (parentPropWithChild?.propertyListElements
					.map((pLE: MaterialPropertyListElement) => pLE.id)
					.indexOf(propertyListElement.id) ?? -1)
		// add property id to copy of parent property list element children to validate circular dependency
		if (indexOfPropertyListElement > -1) {
			parentPropWithChild?.propertyListElements[indexOfPropertyListElement].propertyChildIds.push(
				property.id
			)
		}

		return isCyclicUtil(parentPropWithChild, visited, stack)
	}
</script>

<!-- todo maybe this component can also include an 'Edit' button to edit the property and also the sub properties -> MaterialPropertyModal -->

<div id="autocomplete-qry-properties" style="width: 100%;">
	<AutocompleteInputField
		bind:value={acProperty}
		bind:svelecte
		options={propertyOptions}
		{fontProps}
		autocompleteConfig={propertyAcConfig}
		label={$t('UI.mmpv.properties.name.label')}
		hideDropdownArrow={false}
		onChange={writeSelectedToProperty}
	/>
</div>

<style>
    /* can be treated as css here, since we don't need the mixins in this file */
    @import '$styles/global.scss';
</style>
